import type { ComponentProps, CSSProperties } from "react";

import { cn } from "@v1/ui/utils";
import { APP_THEME_TOKENS, type AppThemeTone } from "./app-theme-tokens";

export type { AppThemeTone } from "./app-theme-tokens";

/**
 * A product surface in the application's own theme, for product shots that
 * must read exactly as the app does. ProductBridge re-inks product tokens
 * through the editorial palette; AppSurface does not. It writes the app's
 * default theme verbatim (Light Blue, or Dark Blue for `tone="dark"`), the
 * app's body typography (Inter at 450, 1.6 leading) and its product radius,
 * and marks the subtree so globals.css can apply the app rules the marketing
 * build would otherwise lack (zero letter-spacing on the text scale, the chat
 * table layout).
 *
 * It holds no state, so a server component can render it: the shot's HTML
 * then arrives complete, and only the product's own client components
 * hydrate.
 */
/**
 * The application's theme and body typography for `tone`: its tokens
 * verbatim, then the app `body` rules (font-sans at weight 450, 1.6
 * leading, antialiased; twin-app/src/app/globals.css).
 */
export function appSurfaceStyle(tone: AppThemeTone): CSSProperties {
  return {
    ...APP_THEME_TOKENS[tone],
    colorScheme: tone,
    fontFamily:
      'var(--font-inter, "Inter Variable"), ui-sans-serif, system-ui, sans-serif',
    fontWeight: 450,
    lineHeight: 1.6,
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    textRendering: "optimizeLegibility",
    "--font-mono":
      'var(--font-jetbrains-mono, "JetBrains Mono"), var(--font-source-code-pro, "Source Code Pro"), ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  } as CSSProperties;
}

export function AppSurface({
  tone = "light",
  className,
  style,
  children,
  ...props
}: ComponentProps<"div"> & { tone?: AppThemeTone }) {
  return (
    <div
      data-product-bridge=""
      data-app-surface={tone}
      className={cn(tone === "dark" ? "dark" : undefined, className)}
      style={{
        ...appSurfaceStyle(tone),
        backgroundColor: "hsl(var(--background))",
        color: "hsl(var(--foreground))",
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}
